/* @flow */
import * as React from 'react';
import { graphql } from 'react-apollo';

import Link from 'components/Link';
import Buttons from 'components/Buttons';
import assertions from 'lib/assertions';
import ToastActions from 'actions/ToastActions';
import BoxBody from 'components/BoxBody';
import Button from 'components/Button';
import Dialog from 'components/Dialog';
import FormElement from 'components/FormElement';
import Input from 'components/Input';
import EventTracking from 'lib/EventTracking';
import { mountModal } from 'lib/modal';
import AdminUpgradeHelpModal from 'admin/components/AdminUpgradeHelpModal';

import REQUEST_ACCESS_FOR_PAID_UPSELL_MUTATION from '../mutations/requestAccessForPaidUpsell.gql';
import ORGANIZATION_REFER_ADMIN_MODAL_MY_DEVICES_QUERY from '../queries/OrganizationReferAdminModalMyDevicesQuery.gql';

import type {
  RequestAccessForPaidUpsellInput,
  requestAccessForPaidUpsell as requestAccessForPaidUpsellMutation,
} from '../mutations/__generated__/requestAccessForPaidUpsell';
import type { OrganizationReferAdminModalMyDevices } from '../queries/__generated__/OrganizationReferAdminModalMyDevices';

type Props = {
  onRequestHide: Function,
  organizationUuid: string,
  data: ReactApolloData<OrganizationReferAdminModalMyDevices>,
  requestAccessForPaidUpsell: RequestAccessForPaidUpsellInput => Promise<{
    data: requestAccessForPaidUpsellMutation,
  }>,
  utmParams?: UTMParams,
};

type State = {
  permission: boolean,
  emailAddress: string,
  disableSubmit: boolean,
  status: 'ask_permission' | 'ask_referral' | 'referred_self',
};

class OrganizationReferAdminModal extends React.Component<Props, State> {
  state: State = {
    permission: false,
    emailAddress: '',
    disableSubmit: false,
    status: 'ask_permission',
  };

  componentWillUpdate(nextProps: Props, nextState: State) {
    if (this.state.status !== 'referred_self' && nextState.status === 'referred_self') {
      EventTracking.trackKey('admin_first_onboarding.admin_email.referred_themselves_modal.view');
    }
  }

  handleReferredSelfReferAnAdmin = () => {
    this.setState({ emailAddress: '', status: 'ask_referral' });
  };

  handleReferredSelfContinue = () => {
    mountModal(
      <AdminUpgradeHelpModal
        organizationUuid={this.props.organizationUuid}
        utmParams={this.props.utmParams}
      />,
      this
    );
    this.props.onRequestHide();
  };

  hasEmailDevice = (email: string): boolean => {
    return !!(this.props.data.devices || []).find(device => {
      return device.type === 'email' && device.endpoint === email;
    });
  };

  submitPermission = (permissionToUseName: boolean) => {
    // first mutation to submit permission to use name
    const { organizationUuid, requestAccessForPaidUpsell } = this.props;
    this.setState({ disableSubmit: true });
    requestAccessForPaidUpsell({
      organizationUuid,
      context: 'unpaid_organization',
      adminReferrals: [],
      permissionToUseName,
    })
      .then(({ data }) => {
        const { error } = data.requestAccessForPaidUpsell;
        if (error) {
          this.setState({ disableSubmit: false });
          ToastActions.showErrorToast(error.message);
        } else {
          this.setState({
            status: 'ask_referral',
            permission: permissionToUseName,
          });
        }
      })
      .catch(e => {
        this.setState({ disableSubmit: false });
        console.error(
          'OrganizationReferAdminModal - error after submitting permission to use name',
          e
        );
        ToastActions.showErrorToast('Oops, something went wrong');
      });
  };

  submitEmailReferral = () => {
    if (this.hasEmailDevice(this.state.emailAddress)) {
      this.setState({ status: 'referred_self' });
      return;
    }

    // second mutation to submit admin's email address
    const { permission, emailAddress } = this.state;
    const { organizationUuid, requestAccessForPaidUpsell } = this.props;
    this.setState({ disableSubmit: true });
    requestAccessForPaidUpsell({
      organizationUuid,
      context: 'unpaid_organization',
      adminReferrals: [emailAddress],
      permissionToUseName: permission,
    })
      .then(({ data }) => {
        const { error } = data.requestAccessForPaidUpsell;
        if (error) {
          this.setState({ disableSubmit: false });
          ToastActions.showErrorToast(error.message);
        } else {
          this.setState({ disableSubmit: false });
          ToastActions.showSuccessToast('Thanks! We’ll be in touch soon.');
          this.props.onRequestHide();
        }
      })
      .catch(e => {
        this.setState({ disableSubmit: false });
        console.error(
          'OrganizationReferAdminModal - error after submitting admin email referral',
          e
        );
        ToastActions.showErrorToast('Oops, something went wrong');
      });
  };

  renderPermissionModal = () => {
    const { disableSubmit } = this.state;
    return (
      <Dialog
        title="Can we use your name when we reach out to your admin?"
        subtext="The Remind team can handle conversations with your admin about upgrading to the School Plan. It always helps when we can do a little name-dropping!"
        onRequestHide={this.props.onRequestHide}
        size="small"
        data-test="permission-modal">
        <BoxBody className="OrganizationReferAdminModal">
          <Button
            data-test="allow-permission-to-use-name-button"
            onClick={() => this.submitPermission(true)}
            disabled={disableSubmit}
            eventKey="admin_first_onboarding.teacher_tour.name_permission.yes.click"
            type="submit"
            full>
            Yes, use my name
          </Button>
          <Button
            level="text"
            onClick={() => this.submitPermission(false)}
            disabled={disableSubmit}
            eventKey="admin_first_onboarding.teacher_tour.name_permission.no.click"
            type="submit">
            No, don’t use my name
          </Button>
        </BoxBody>
      </Dialog>
    );
  };

  renderReferredSelf = () => {
    return (
      <Dialog
        size="small"
        title="You entered your own email address."
        subtext={
          <span>
            <Link
              eventKey="admin_first_onboarding.admin_email.referred_themselves_modal.learn_more.click"
              target="_blank"
              href="/plans">
              Remind Plans
            </Link>{' '}
            can only be purchased by administrators.
          </span>
        }>
        <BoxBody className="OrganizationReferAdminModal">
          <Buttons align="center">
            <Button
              data-test="refer-myself-button"
              level="secondary"
              eventKey="admin_first_onboarding.admin_email.referred_themselves_modal.continue.click"
              onClick={this.handleReferredSelfContinue}>
              I am an admin
            </Button>
            <Button
              data-test="refer-an-admin-button"
              level="primary"
              eventKey="admin_first_onboarding.admin_email.referred_themselves_modal.refer_an_admin.click"
              onClick={this.handleReferredSelfReferAnAdmin}>
              Refer an admin
            </Button>
          </Buttons>
        </BoxBody>
      </Dialog>
    );
  };

  renderEmailReferralModal = () => {
    const { disableSubmit, permission } = this.state;
    const title = permission
      ? 'Thanks! What’s your admin’s email address?'
      : 'Got it, we won’t mention you. What’s your admin’s email address?';
    return (
      <Dialog
        title={title}
        onRequestHide={this.props.onRequestHide}
        size="small"
        onDialogDismiss={() => {
          EventTracking.trackKey('admin_first_onboarding.admin_email.close.click');
          this.props.onRequestHide();
        }}
        data-test="email-referral-modal">
        <BoxBody className="OrganizationReferAdminModal">
          <FormElement
            label="Admin’s email address"
            labelHidden
            showErrorAfterBlur
            validate={e => {
              assertions.email(e.target.value, 'Invalid email address.');
            }}
            onValidChange={i =>
              this.setState({ emailAddress: i.target.value, disableSubmit: false })
            }
            onInvalidChange={() => this.setState({ emailAddress: undefined, disableSubmit: true })}
            element={Input}
            elementProps={{
              placeholder: 'joe@msjhs.edu',
            }}
          />
          <Button
            eventKey="admin_first_onboarding.admin_email.submit.click"
            onClick={() => this.submitEmailReferral()}
            disabled={disableSubmit}
            type="submit"
            data-test="submit-email-referral-button">
            Submit
          </Button>
        </BoxBody>
      </Dialog>
    );
  };

  render() {
    if (this.state.status === 'ask_permission') {
      return this.renderPermissionModal();
    } else if (this.state.status === 'ask_referral') {
      return this.renderEmailReferralModal();
    } else if (this.state.status === 'referred_self') {
      return this.renderReferredSelf();
    } else {
      return null;
    }
  }
}

const withData = graphql(ORGANIZATION_REFER_ADMIN_MODAL_MY_DEVICES_QUERY);

const withMutations = graphql(REQUEST_ACCESS_FOR_PAID_UPSELL_MUTATION, {
  props({ mutate }) {
    return {
      requestAccessForPaidUpsell(input: RequestAccessForPaidUpsellInput) {
        return mutate({
          variables: { input },
        });
      },
    };
  },
});

export default withData(withMutations(OrganizationReferAdminModal));
export { OrganizationReferAdminModal };
